/* eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';
import { takeLatest, call, all, select, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Components
import { ModalBulkErrors } from 'components/ModalBulkErrors';

// Services
import { notificationsApi } from 'services/apiDigitalTicket/notifications';
import { auth } from 'services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

// Helpers
import { isObject } from 'helpers/isObject';

// Constants
import ENVS from 'constants/enviroments';
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

// Store
import store from 'store';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as NotificationsActions from './actions';

// Types
import { NotificationsTypes } from './types';

function* listNotifications({
  payload,
}: ReturnType<typeof NotificationsActions.listNotificationsRequest>): any {
  try {
    const { body, filtering } = payload;
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando notificações' }),
    );

    const transportOperator = yield select(
      (state: any) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(notificationsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      if (isObject(response.data)) {
        yield put(
          NotificationsActions.listNotificationsSuccess({
            notifications: response.data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }

      if (Array.isArray(response.data)) {
        yield put(
          NotificationsActions.listNotificationsSuccess({
            notifications: response.data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - listNotifications - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.listNotificationsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof NotificationsActions.getNotificationRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state: any) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(notificationsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        NotificationsActions.getNotificationSuccess({
          notificationData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - getNotification - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.getNotificationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof NotificationsActions.createNotificationRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando notificação' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(notificationsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(NotificationsActions.createNotificationSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Notificação criada com sucesso',
        }),
      );
      yield put(push(PATH.NOTIFICATIONS.BASE_URL));

      yield put(NotificationsActions.listNotificationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - createNotification - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.createNotificationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof NotificationsActions.updateNotificationRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando notificação' }));
    const response = yield call(notificationsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(NotificationsActions.updateNotificationSuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Notificação editada com sucesso',
        }),
      );
      yield put(push(PATH.NOTIFICATIONS.BASE_URL));

      yield put(NotificationsActions.listNotificationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - updateNotification - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.updateNotificationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof NotificationsActions.deleteNotificationRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo notificação' }),
    );
    const response = yield call(notificationsApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(NotificationsActions.deleteNotificationSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Notificação excluída com sucesso',
        }),
      );
      yield put(NotificationsActions.listNotificationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - deleteNotification - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.deleteNotificationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
}: ReturnType<typeof NotificationsActions.createNotificationsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando notificações' }),
    );
    const response = yield call(notificationsApi.create, {
      data: [...payload],
    });

    if (response.status === 200) {
      yield put(NotificationsActions.createNotificationsSuccess());

      yield all(
        response.data.success.map(({ email }: any) =>
          call(() =>
            sendPasswordResetEmail(auth, email, {
              url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
            }),
          ),
        ),
      );

      if (response.data.success.length > 0) {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: `${response.data.success.length} Notificações cadastradas com sucesso.`,
          }),
        );
      }

      if (response.data.error.length > 0) {
        yield put(DialogsActions.setClose());

        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(NotificationsActions.listNotificationsRequest({}));
            },
          }),
        );
      } else {
        yield put(DialogsActions.setClose());

        yield put(NotificationsActions.listNotificationsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - createNotification - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.createNotificationsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateList({
  payload,
}: ReturnType<typeof NotificationsActions.updateNotificationsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando notificações' }),
    );
    const response = yield call(notificationsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(NotificationsActions.updateNotificationsSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(NotificationsActions.listNotificationsRequest({}));
            },
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Notificações editadas com sucesso',
          }),
        );
        yield put(NotificationsActions.listNotificationsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - updateNotifications - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.updateNotificationsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeList({
  payload,
}: ReturnType<typeof NotificationsActions.deleteNotificationsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo notificações' }),
    );
    
    const { ids } = payload as { ids: string[] };

    const responses = yield all(
      ids.map((id) =>
        call(notificationsApi.delete, { params: { id } }),
      ),
    );

    const allSucceeded = responses.every((r: { status: number }) => r.status === 200);

    if (allSucceeded) {
      yield put(NotificationsActions.deleteNotificationsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Notificações excluídas com sucesso',
        }),
      );
      yield put(NotificationsActions.listNotificationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('notifications - deleteNotifications - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(NotificationsActions.deleteNotificationsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-profile import/prefer-default-export
export const notificationsSagas = [
  takeLatest(NotificationsTypes.LIST_NOTIFICATIONS_REQUEST, listNotifications),
  takeLatest(NotificationsTypes.GET_NOTIFICATION_REQUEST, get),
  takeLatest(NotificationsTypes.CREATE_NOTIFICATION_REQUEST, create),
  takeLatest(NotificationsTypes.UPDATE_NOTIFICATION_REQUEST, update),
  takeLatest(NotificationsTypes.DELETE_NOTIFICATION_REQUEST, remove),
  takeLatest(NotificationsTypes.CREATE_NOTIFICATIONS_REQUEST, createList),
  takeLatest(NotificationsTypes.UPDATE_NOTIFICATIONS_REQUEST, updateList),
  takeLatest(NotificationsTypes.DELETE_NOTIFICATIONS_REQUEST, removeList),
];
